<template>
  <!-- 单根：避免 el-tooltip 等只认一个触发节点；Teleport 放在根内仍挂到 body -->
  <div
    class="custom-popover-root"
    :class="{ 'custom-popover-root--fill': fillParent }"
  >
    <div
      ref="triggerRef"
      class="custom-popover-trigger fr-ac pos-r"
      :class="{ opa4: disabled, 'custom-popover-trigger--fill': fillParent }"
      @click.stop="showModelClick"
      @mousedown.stop
      @pointerdown.stop
    >
      <slot></slot>
    </div>
    <Teleport to="body">
      <div class="popover-overlay" v-if="modelValue" @click="hideComponent">
        <div
          ref="popoverRef"
          class="popover-box"
          :class="{ 'popover-top': showOnTop }"
          :style="popoverStyle"
          @click.stop
        >
          <div class="text mb-16">{{ content }}</div>
          <div v-if="$slots.sub" class="sub-wrap mb-12">
            <slot name="sub"></slot>
          </div>
          <div v-else-if="subContent" class="sub-text mb-12">
            {{ subContent }}
          </div>
          <div class="fr-ac">
            <div class="cancel-btn" @click="clickLeftBtn">
              {{ leftBtnName }}
            </div>
            <div class="confirm-btn" @click="clickRightBtn">
              {{ rightBtnName }}
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import {
  ref,
  computed,
  onMounted,
  onUnmounted,
  nextTick,
  type PropType,
} from 'vue';
import { ElMessage } from 'element-plus';
const emit = defineEmits([
  'update:modelValue',
  'rightClick',
  'leftLeft',
  'otherClick',
]);

const props = defineProps({
  content: { type: String, default: '' },
  /** 主文案下方的补充说明（与 #sub 插槽二选一，插槽优先） */
  subContent: { type: String, default: '' },
  leftBtnName: { type: String, default: '取消' },
  rightBtnName: { type: String, default: '删除' },
  isLeftOffset: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  disabledText: { type: String, default: '暂无权限' },
  placement: { type: String, default: 'bottom' }, // 'bottom', 'top', 'auto'
  arrowOffset: { type: Number, default: 0 }, // 箭头水平偏移量（像素）
  /** 打开气泡前调用（用于写入待删除项等上下文） */
  beforeOpen: { type: Function as PropType<() => void>, default: undefined },
  /** 为 true 时在定宽容器（如直播间/角色卡片 .delete-btn）内铺满 100% */
  fillParent: { type: Boolean, default: false },
});

const modelValue = ref(false);
const triggerRef = ref<HTMLElement | null>(null);
const popoverRef = ref<HTMLElement | null>(null);
const popoverPosition = ref({ left: 0, top: 0 });
const showOnTop = ref(false);
const popoverHeight = ref(100);

const POPOVER_OFFSET = 10; // 弹窗与触发元素的间距

const popoverStyle = computed(() => {
  return {
    left: `${popoverPosition.value.left}px`,
    top: `${popoverPosition.value.top}px`,
  };
});

const calculatePosition = async () => {
  if (!triggerRef.value) return;

  const rect = triggerRef.value.getBoundingClientRect();
  const viewportHeight = window.innerHeight;
  const viewportWidth = window.innerWidth;

  // 获取弹窗实际高度/宽度
  await nextTick();
  let popoverWidth = 180;
  if (popoverRef.value) {
    popoverHeight.value = popoverRef.value.offsetHeight || 100;
    popoverWidth = popoverRef.value.offsetWidth || 180;
  }

  // 计算下方空间是否足够
  const spaceBelow = viewportHeight - rect.bottom;
  const spaceAbove = rect.top;

  // 判断显示位置
  if (props.placement === 'auto') {
    showOnTop.value =
      spaceBelow < popoverHeight.value + POPOVER_OFFSET &&
      spaceAbove > spaceBelow;
  } else if (props.placement === 'top') {
    showOnTop.value = true;
  } else {
    showOnTop.value = false;
  }

  // 以 trigger 中心为基准，减去弹窗自身宽度的一半，实现真正居中
  let idealLeft =
    rect.left + rect.width / 2 - popoverWidth / 2 + props.arrowOffset;

  // 边界保护：避免超出视口左右两侧
  if (idealLeft < 10) idealLeft = 10;
  if (idealLeft + popoverWidth > viewportWidth - 10) {
    idealLeft = viewportWidth - popoverWidth - 10;
  }

  if (showOnTop.value) {
    // 显示在上方：弹窗底部对齐按钮顶部上方
    popoverPosition.value = {
      left: idealLeft,
      top: rect.top - popoverHeight.value - POPOVER_OFFSET,
    };
  } else {
    // 显示在下方：弹窗顶部对齐按钮底部下方
    popoverPosition.value = {
      left: idealLeft,
      top: rect.bottom + POPOVER_OFFSET,
    };
  }
};

const hideComponent = () => {
  modelValue.value = false;
  emit('otherClick');
};

const showModelClick = () => {
  if (props.disabled) return ElMessage.warning(props.disabledText);
  props.beforeOpen?.();
  // 先展示再计算位置，否则 popoverRef 不存在、高度不准；且层级需高于页面壳层（如 zi-99999）
  modelValue.value = true;
  nextTick(() => {
    calculatePosition();
  });
};

const clickRightBtn = () => {
  modelValue.value = false;
  emit('rightClick');
};

const clickLeftBtn = () => {
  modelValue.value = false;
  emit('leftLeft');
};

// 监听窗口大小变化和滚动
const handleResize = () => {
  if (modelValue.value) {
    calculatePosition();
  }
};

onMounted(() => {
  window.addEventListener('resize', handleResize);
  window.addEventListener('scroll', handleResize, true);
});

onUnmounted(() => {
  window.removeEventListener('resize', handleResize);
  window.removeEventListener('scroll', handleResize, true);
});
</script>

<style lang="scss" scoped>
.popover-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  /* 显著高于 .zi-99999 / 各类弹层，避免气泡被主页壳层或 WebView 叠层遮挡（与分类删除同类问题） */
  z-index: 500000;
  background: transparent;
}

.popover-box {
  position: fixed;
  z-index: 500001;
  padding: 22px 20px;
  min-width: 180px;
  max-width: min(320px, calc(100vw - 24px));
  border-radius: var(--border-radius);
  // background: linear-gradient(180deg, #111b37 0%, #091023 100%);
  background: var(--bg-color-white);
  backdrop-filter: blur(10px);
  box-shadow: var(--selector-box-shadow);
}

// 下方显示时的箭头
.popover-box::before {
  content: '';
  position: absolute;
  left: 50%;
  top: -8px;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-bottom: 8px solid var(--bg-color-white);
}

// 上方显示时的样式
.popover-top {
  &::before {
    display: none;
  }

  &::after {
    content: '';
    position: absolute;
    left: 50%;
    bottom: -8px;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-top: 8px solid var(--bg-color-white);
  }
}

.text {
  color: var(--text-color);
  font-size: 14px;
  font-weight: 400;
  text-align: center;
}

.sub-wrap,
.sub-text {
  color: var(--text-color);
  opacity: 0.85;
  font-size: 12px;
  line-height: 1.5;
  text-align: center;
  word-break: break-word;
}

.cancel-btn {
  width: 64px;
  height: 28px;
  line-height: 28px;
  text-align: center;
  background: var(--btn-bg-color-grey);
  border-radius: var(--border-radius);
  font-size: 12px;
  color: var(--text-color);
  margin-right: 12px;
  cursor: pointer;
}

.confirm-btn {
  width: 64px;
  height: 28px;
  line-height: 28px;
  text-align: center;
  background: var(--btn-bg-color-primary);
  border-radius: var(--border-radius);
  font-size: 12px;
  color: #ffffff;
  cursor: pointer;
}

.fr-ac {
  display: flex;
  align-items: center;
  justify-content: center;
}

.pos-r {
  position: relative;
}

.opa4 {
  opacity: 0.4;
}

.custom-popover-root {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  max-width: 100%;
  min-width: 0;
  vertical-align: middle;
}

.custom-popover-root--fill {
  width: 100%;
  height: 100%;
}

.custom-popover-trigger {
  max-width: 100%;
}

.custom-popover-trigger--fill {
  width: 100%;
  height: 100%;
}
</style>
